<usb-flash-panel>
  <div class="card border-primary mb-4 mt-4 shadow-sm flash-panel" if={ props.firmware }>
    <!-- Panel Header -->
    <div class="card-header bg-primary text-white d-flex justify-content-between align-items-center">
      <h5 class="mb-0">
        <i class="bi bi-usb-symbol me-2"></i>
        Programar por USB: { props.firmware.name } (v{ props.firmware.version })
      </h5>
      <button type="button" class="btn-close btn-close-white" aria-label="Close" onclick={ handleClose } disabled={
        state.isFlashing }></button>
    </div>

    <div class="card-body">
      <!-- Unsupported Browser Warning -->
      <div if={ !state.isSupported } class="alert alert-warning">
        <h5 class="alert-heading"><i class="bi bi-exclamation-triangle-fill me-2"></i>Navegador no compatible</h5>
        <p>{ state.unsupportedReason }</p>
        <hr>
        <p class="mb-0 small">Para usar esta función, accede desde Google Chrome o Microsoft Edge en tu ordenador
          (HTTPS necesario).</p>
      </div>

      <div if={ state.isSupported }>
        <!-- Main Action Area (when idle or error) -->
        <div if={ state.flashState===FlashState.IDLE || state.flashState===FlashState.ERROR } class="text-center py-4">
          <i class="bi bi-cpu mb-3 text-secondary" style="font-size: 3rem;"></i>
          <h5>Instrucciones para programar</h5>
          <ul class="text-start d-inline-block text-muted mb-4">
            <li>1. Conecta el dispositivo Mercury al ordenador por USB.</li>
            <li>2. Haz clic en "Programar Firmware".</li>
            <li>3. Selecciona el puerto USB correcto en la ventana emergente.</li>
            <li>4. <strong>Atención:</strong> Si falla, mantén presionado BOOT y toca RESET en la placa.</li>
          </ul>
          <br>
          <button class="btn btn-primary app-btn app-btn-lg" onclick={ startFlashing }
            disabled={ state.isDownloading }>
            <span if={ state.isDownloading } class="spinner-border spinner-border-sm me-2" role="status"></span>
            <i if={ !state.isDownloading } class="bi bi-lightning-fill me-2"></i>
            { state.isDownloading ? 'Descargando binario...' : 'PROGRAMAR FIRMWARE' }
          </button>
        </div>

        <!-- Success Area -->
        <div if={ state.flashState===FlashState.DONE } class="text-center py-5">
          <div class="mb-3">
            <i class="bi bi-check-circle-fill text-success" style="font-size: 4rem;"></i>
          </div>
          <h4 class="text-success mb-3">¡Programación completada!</h4>
          <p class="text-muted">El dispositivo se ha reiniciado con el nuevo firmware.</p>
          <button class="btn btn-outline-primary app-btn app-btn-md mt-3" onclick={ resetPanel }>
            <i class="bi bi-arrow-repeat"></i>
            Programar otro dispositivo
          </button>
        </div>

        <!-- Progress and Status Area (when flashing, downloading, or error) -->
        <div if={ state.flashState !==FlashState.IDLE && state.flashState !==FlashState.DONE }>

          <!-- Current status text -->
          <div class="d-flex justify-content-between align-items-end mb-2">
            <h6 class="mb-0 text-{ getStatusColor() } fw-bold">
              { getStatusLabel() }
            </h6>
            <span class="text-muted small">{ state.progress }%</span>
          </div>

          <!-- Progress bar -->
          <div class="progress mb-3" style="height: 12px;">
            <div class="progress-bar progress-bar-striped progress-bar-animated usb-flash-progress-bar bg-{ getStatusColor() }"
              role="progressbar" aria-valuenow={ state.progress } aria-valuemin="0"
              aria-valuemax="100"></div>
          </div>

          <!-- Detailed message -->
          <div class="alert alert-{ getStatusColor() } py-2 mb-3">
            <i class="bi bi-info-circle me-2"></i>
            { state.message }
          </div>

          <!-- Chip Info (if detected) -->
          <div if={ state.chipInfo } class="mb-3 text-muted small">
            <strong>Chip detectado:</strong> { state.chipInfo }
          </div>

          <!-- Error handling -->
          <div if={ state.flashState===FlashState.ERROR } class="mt-4 text-center">
            <button class="btn btn-outline-danger app-btn app-btn-md" onclick={ resetPanel }>
              <i class="bi bi-arrow-counterclockwise me-2"></i>Ver instrucciones y reintentar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <script>
    import { ToastService } from '../services/toast-service';
    import { SerialFlashService, FirmwareDownloadService, FlashState, FLASH_STATE_LABELS, FLASH_STATE_COLORS } from '../services/flasher';

    export default {
      state: {
        isSupported: false,
        unsupportedReason: '',

        flashState: FlashState.IDLE,
        progress: 0,
        message: 'Listo para comenzar.',
        chipInfo: '',

        isDownloading: false,
        isFlashing: false,
      },

      // Exportar enums al template
      FlashState: FlashState,

      onMounted() {
        // Verificar si el navegador lo soporta
        const supported = SerialFlashService.isSupported();
        this.update({
          isSupported: supported,
          unsupportedReason: supported ? '' : SerialFlashService.getUnsupportedReason()
        });

        this.syncProgressBarWidth();
      },

      onUpdated() {
        this.syncProgressBarWidth();
      },

      getStatusLabel() {
        return FLASH_STATE_LABELS[this.state.flashState] || 'Desconocido';
      },

      getStatusColor() {
        return FLASH_STATE_COLORS[this.state.flashState] || 'secondary';
      },

      syncProgressBarWidth() {
        const progressBar = this.root?.querySelector('.usb-flash-progress-bar');
        if (progressBar) {
          progressBar.style.width = `${this.state.progress}%`;
        }
      },

      handleClose() {
        if (this.state.isFlashing) return;

        this.resetPanel();
        if (this.props.onclose) {
          this.props.onclose();
        }
      },

      resetPanel() {
        this.update({
          flashState: FlashState.IDLE,
          progress: 0,
          message: 'Listo para comenzar.',
          chipInfo: '',
          isDownloading: false,
          isFlashing: false
        });
      },

      async startFlashing() {
        // 1. Validar props
        if (!this.props.firmware || !this.props.firmware.url) {
          ToastService.show('Error: URL del firmware no encontrada.', 'danger');
          return;
        }

        this.update({ isDownloading: true, message: 'Descargando firmware...' });

        let binaryData;
        try {
          // 2. Descargar binario
          binaryData = await FirmwareDownloadService.downloadBinary(this.props.firmware.url);
        } catch (error) {
          console.error('Error downloading firmware:', error);
          this.update({
            isDownloading: false,
            flashState: FlashState.ERROR,
            message: error.message || 'Error al descargar el archivo de firmware.'
          });
          return;
        }

        // Descarga completada, iniciar flujo de flasheo serie
        this.update({
          isDownloading: false,
          isFlashing: true,
          flashState: FlashState.CONNECTING,
          progress: 0,
          message: 'Solicitando puerto serie...'
        });

        try {
          // 3. Ejecutar flasheo con progreso
          await SerialFlashService.flash(
            binaryData,
            (progressUpdate) => {
              // Actualizar el estado con los avances
              this.update({
                flashState: progressUpdate.state,
                progress: progressUpdate.progress,
                message: progressUpdate.message,
                chipInfo: progressUpdate.chipInfo || this.state.chipInfo,
              });
            }
          );

          // Si llega aquí, todo fue bien
          this.update({ isFlashing: false });

        } catch (error) {
          console.error('Flashing error:', error);
          // El callback de progreso ya habrá seteado el estado ERROR y el mensaje con los detalles,
          // pero aseguramos por si acaso
          this.update({
            isFlashing: false,
            // Si el error ocurrió muy rápido o fuera del flujo, aseguramos estado
            flashState: this.state.flashState !== FlashState.ERROR ? FlashState.ERROR : this.state.flashState
          });
        }
      }
    };
  </script>
</usb-flash-panel>
